type ConnectionStateChanged = (newState: boolean) => void;
type OnDeviceMessageHandler = (message: Uint8Array) => void;
type OnStringDeviceMessageHandler = (message: string) => void;

interface PortData {
  id?: string,
  vendor?: string,
  product?: string,
  port?: SerialPort,
  usbVendorId?: number,
  usbProductId?: number,
  physicallyConnected?: boolean,
  open: boolean,
  _reader?: ReadableStreamReader<Uint8Array>,
  options: {
    baudRate: number,
    dataBits: 7 | 8,
    bufferSize: number,
    flowControl: "none" | "hardware",
    parity: "none" | "even" | "odd",
    stopBits: 1 | 2;
  },
};

const encoder = new TextEncoder();

const portData: PortData = {
  id: "",
  port: undefined,
  physicallyConnected: false,
  open: false,
  options: {
    baudRate: 115200,
    bufferSize: 255,
    dataBits: 8,
    flowControl: "none",
    parity: "none",
    stopBits: 1
  },
};

export const selectPortAsync: (vendorId?: number, productId?: number) => Promise<boolean> = async (vendorId?: number, productId?: number) => {
  try {
    console.log("selecting port", vendorId, productId);
    if (!navigator.serial) {
      return false;
    }

    const filters = (!!vendorId) ? {
      filters: [{ usbVendorId: vendorId, usbProductId: (productId ?? undefined) }]
    } : undefined;
    const port = await navigator.serial.requestPort(filters);
    const info = port.getInfo();
    portData.usbVendorId = info.usbVendorId;
    portData.usbProductId = info.usbProductId;
    console.log("port selected", info);


    return true;
  }
  catch (e) {
    console.log(e);
    return false;
  }
};

export const connectAsync: (onDeviceMessage: OnDeviceMessageHandler, onConnectionStateChanged?: ConnectionStateChanged) => Promise<void> = async (onDeviceMessage: OnDeviceMessageHandler, onConnectionStateChanged?: ConnectionStateChanged) => {
  await initAsync(onDeviceMessage, onConnectionStateChanged);
  console.log("connecting to port", portData.port);
  
  const { port, id, options } = portData;

  if (!port) {
    return;
  }

  try {
    console.log(id + ": opening");
    await port.open(options as SerialOptions);
    console.log(id + ": opened");
    const open = !!port.readable;
    portData.open = open;
    if (!!onConnectionStateChanged) {
      onConnectionStateChanged(open);
    }

    // note: no await!
    monitorAsync(onDeviceMessage, onConnectionStateChanged);
    return;
  }
  catch (e) {
    console.error(e);
    if (!!onConnectionStateChanged) {
      onConnectionStateChanged(false);
    }
    throw e;
  }
};

export const writeAsync: (data: string) => Promise<void> = async (data: string) => {
  console.log("write data to port'", data, "'");
  const writable = portData.port?.writable;
  if (writable) {
    const writer = writable.getWriter();
    await writer.write(encoder.encode(data));
    writer.releaseLock();
  }
};

export const closeAsync = async (onConnectionStateChanged: ConnectionStateChanged) => {
  var port = portData.port;

  console.log("closing port", port);
  const reader = portData._reader;
  const id = portData.id;
  portData.open = false;
  if (!!onConnectionStateChanged) {
    onConnectionStateChanged(false);
  }
  try {
    if (reader) {
      console.log("canceling reader");
      await reader.cancel();
      reader.releaseLock();
      portData._reader = null;
    }
    else {
      console.log("no reader to cancel, closing port");
      port?.close();
    }
    console.log(id + ": port closed");
  }
  catch (e) {
    console.error("closing error", e);
  }
};

const decoder = new TextDecoder("utf-8");
export const bufferChunks = (callback: OnDeviceMessageHandler, timeOutMs = 200) => {
  let buffer: any[] = [];
  let sendTimeoutId: NodeJS.Timeout;

  return (bytes: Uint8Array) => {
    buffer = buffer.concat(Array.from(bytes));

    clearTimeout(sendTimeoutId);
    sendTimeoutId = setTimeout(async () => {
      await new Promise<void>((resolve) => {

        callback(Uint8Array.from(buffer));

        resolve();
        buffer = [];
      });
    }, timeOutMs);
  };
};

export const bufferChunksAsString = (callback: OnStringDeviceMessageHandler, timeOutMs = 200) => {
  let buffer: any[] = [];
  let sendTimeoutId: NodeJS.Timeout;

  return (bytes: Uint8Array) => {
    buffer = buffer.concat(Array.from(bytes));

    clearTimeout(sendTimeoutId);
    sendTimeoutId = setTimeout(async () => {
      await new Promise<void>((resolve) => {
        var rawText = decoder.decode(Uint8Array.from(buffer));
        callback(rawText);

        resolve();
        buffer = [];
      });
    }, timeOutMs);
  };
};

///////////////////////// Internal

const onDisconnectAsync = async (id: string, onConnectionStateChanged: ConnectionStateChanged) => {
  console.log(id + "device disconnected", portData.port);
  portData.physicallyConnected = false;
  await closeAsync(onConnectionStateChanged);
};

const onConnectAsync = async (e: Event, id: string, onConnectionStateChanged: ConnectionStateChanged, onNewDeviceMessage: OnDeviceMessageHandler) => {
  console.log(id + "device connected", e);
  const port = e.target as SerialPort;
  portData.port = port;
  portData.physicallyConnected = true;
  await connectAsync(onNewDeviceMessage, onConnectionStateChanged);
};

const initAsync: (onNewDeviceMessage: OnDeviceMessageHandler, onConnectionStateChanged?: ConnectionStateChanged) => Promise<boolean> = async (onNewDeviceMessage: OnDeviceMessageHandler, onConnectionStateChanged?: ConnectionStateChanged) => {
  console.log("initializing port", portData.port);
  await closeAsync(onConnectionStateChanged);

  const ports = await navigator.serial.getPorts();
  const id = portData.usbVendorId + ":" + portData.usbProductId;

  const foundPort = ports.find((port) => {
    const portInfo = port.getInfo();
    return portInfo.usbVendorId === portData.usbVendorId && portInfo.usbProductId === portData.usbProductId;
  });
  if (!foundPort) {
    //window.location.search = ``
    return false;
  }

  portData.port = foundPort;
  portData.id = id;
  portData.physicallyConnected = true;

  // notification for a USB device getting physically connected

  navigator.serial.addEventListener("connect", async e => await onConnectAsync(e, id, onConnectionStateChanged, onNewDeviceMessage));
  navigator.serial.addEventListener("disconnect", async () => await onDisconnectAsync(id, onConnectionStateChanged));
  return true;
};

const monitorAsync = async (onDeviceMessage: OnDeviceMessageHandler, onConnectionStateChanged?: ConnectionStateChanged) => {
  while (portData.open && portData.port?.readable) {
    portData.open = true;
    if (!!onConnectionStateChanged) {
      onConnectionStateChanged(true);
    }
    const reader = portData.port!.readable!.getReader();
    portData._reader = reader;
    try {
      while (portData.open) {
        console.log("reading...");
        const { value, done } = await reader.read();
        if (done) {
          // |reader| has been canceled.
          portData.open = false;
          if (!!onConnectionStateChanged) {
            onConnectionStateChanged(false);
          }
          break;
        }
        onDeviceMessage(value);
      }
      console.log("reader done");
      await closeAsync(onConnectionStateChanged);
    } catch (error) {
      console.error("reading error", error);
    } finally {
      reader.releaseLock();
      console.log("reader released");
    }
  }
};